import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';

import { humanizeKey } from '../utils/humanizeKey.js';
import DisplaySettingField from './DisplaySettingField.jsx';

/**
 * `HOW IT LOOKS` — the chosen type's own options (CT-5579).
 *
 * The SETTINGS block beside it is the same two controls for all 28 types:
 * end-user filters and the Metabase link. This is the half that differs — a
 * KPI's trend icon and comparison mode, a table's density and sticky header, a
 * donut's inner radius — and until now none of it was rendered, although every
 * type declares it and `visualizationConfig.display` has always persisted it.
 *
 * Read from the type's schema, so it follows the catalog: 201 controls across
 * the 28 types today, and a property Data adds tomorrow appears without a line
 * changing here. The 18 it cannot render yet are named in `readDisplaySettings`.
 *
 * Grouped the way the schema nests them, with the ungrouped ones first: a
 * caption and a comparison mode belong to the widget, where a title and a
 * message belong to `empty_state`, and flattening the two together would read
 * as one list of unrelated fields.
 */
function WidgetDisplaySettings({ fields, values, onChange }) {
  const { t } = useTranslation('dashboards');

  /** `[{ group, fields }]` — ungrouped first, then each group once, in schema order. */
  const groups = useMemo(() => {
    const order = [];
    const byGroup = new Map();

    fields.forEach((field) => {
      const key = field.group ?? '';
      if (!byGroup.has(key)) {
        byGroup.set(key, []);
        order.push(key);
      }
      byGroup.get(key).push(field);
    });

    return order
      .sort((a, b) => (a === '' ? -1 : b === '' ? 1 : 0))
      .map((group) => ({ group, fields: byGroup.get(group) }));
  }, [fields]);

  if (!fields.length) return null;

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col">
        <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-content-secondary">
          {t('widgetBuilder.displaySettings.title')}
        </h3>
        <p className="m-0 text-xs text-content-muted">
          {t('widgetBuilder.displaySettings.subtitle')}
        </p>
      </header>

      {groups.map(({ group, fields: groupFields }) => (
        <div key={group || 'root'} className="flex flex-col gap-3">
          {group ? (
            <span className="text-xs font-medium text-content-secondary">
              {t(`widgetBuilder.displaySettings.groups.${group}`, {
                defaultValue: humanizeKey(group),
              })}
            </span>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {groupFields.map((field) => (
              <DisplaySettingField
                key={field.path}
                field={field}
                value={values[field.path]}
                onChange={onChange}
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

WidgetDisplaySettings.propTypes = {
  /** `readDisplaySettings().fields` for the selected type. */
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** `{ path: value }`, the flat form state. */
  values: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default WidgetDisplaySettings;
